import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import qs from 'qs';
import {
	Router,
	NavigationEnd,
	ActivatedRoute,
	NavigationStart
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter, map, mergeMap } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import store from 'store';
import * as SettingsActions from './store/settings/actions';
import * as Reducers from './store/reducers';

import english from './locales/en-US';
import german from './locales/de-DE';
import { Subscription } from 'rxjs';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import { enUS, de } from 'date-fns/locale';

const locales = {
	'en-US': english,
	'de-DE': german,
};


//
// TODO: Cleanup Constructor, ngOnInit!
//
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
	_locale: string;
	_theme: string;

	private subscription = new Subscription();

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private titleService: Title,
		private nzi18n: NzI18nService,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		private store: Store<any>,
		translate: TranslateService
	) {
		Object.keys(locales).forEach(locale => {
			translate.setTranslation(locale, locales[locale]);
		});
		translate.setDefaultLang('de-DE');

		// localization && theme listener
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			if (this._locale !== state.locale) {
				translate.use(state.locale);
				this.setNzLocale(state.locale);
			}
			if (this._theme !== state.theme) {
				this.setTheme(state.theme);
			}
			this._locale = state.locale;
			this._theme = state.theme;
		}));
	}

	private setNzLocale(locale: string): void {
		switch (locale) {
			case 'de-DE':
				//this.nzi18n.setLocale(de_DE);
				this.nzi18n.setDateLocale(de);
				break;
			case 'en-US':
			default:
				//this.nzi18n.setLocale(en_US);
				this.nzi18n.setDateLocale(enUS);
				break;
		}
	}

	ngOnInit(): void {
		// set page title from router data variable
		this.subscription.add(this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map(route => {
					while (route.firstChild) {
						route = route.firstChild;
					}
					return route;
				}),
				map(route => {
					while (route.firstChild) {
						route = route.firstChild;
					}
					return route;
				}),
				filter(route => route.outlet === 'primary'),
				mergeMap(route => route.data)
			)
			.subscribe(event =>
				this.titleService.setTitle('Generische Gesundheitsplattform | ' + event['title'])
			));

		// listen url query params and set them to ngrx store
		this.subscription.add(this.router.events
			.pipe(filter(event => event instanceof NavigationStart))
			.subscribe((event: NavigationStart) => {
				const queryString = event.url.match(/\?(.*)/);
				if (queryString) {
					const queryParams = qs.parse(queryString[1]);
					const keys = Object.keys(queryParams);
					if (keys.length) {
						keys.forEach(key => {
							let value;
							switch (queryParams[key]) {
								case 'false':
									value = false;
									break;
								case 'true':
									value = true;
									break;
								default:
									value = queryParams[key];
									break;
							}
							this.store.dispatch(
								new SettingsActions.SetStateAction({
									[key]: value
								})
							);
						});
					}
				}
			}));

		// detecting & set mobile/tablet/desktop viewports
		const setViewPort = (
			isMobileView = false,
			isTabletView = false
		) => {
			this.store.dispatch(
				new SettingsActions.SetStateAction({
					isMobileView
				})
			);
			this.store.dispatch(
				new SettingsActions.SetStateAction({
					isTabletView
				})
			);
		};
		const detectViewPort = (load = false) => {
			const _isMobileView = window.innerWidth < 768;
			const _isTabletView = window.innerWidth < 992;
			const _isDesktopView = !_isMobileView && !_isTabletView;
			const isMobileView = store.get('app.settings.isMobileView');
			const isTabletView = store.get('app.settings.isTabletView');
			const isDesktopView = !isMobileView && !isTabletView;
			if (_isDesktopView && (_isDesktopView !== isDesktopView || load)) {
				setViewPort(false, false);
			}
			if (
				_isTabletView &&
				!_isMobileView &&
				(_isTabletView !== isTabletView || load)
			) {
				setViewPort(false, true);
				this.store.dispatch(
					new SettingsActions.SetStateAction({
						isMenuCollapsed: true
					})
				);
			}
			if (_isMobileView && (_isMobileView !== isMobileView || load)) {
				setViewPort(true, false);
			}
		};
		detectViewPort(true);
		window.addEventListener('resize', () => {
			detectViewPort();
		});

		// set primary color on app load
		const primaryColor = () => {
			const color = store.get('app.settings.primaryColor');
			if (color) {
				const addStyles = () => {
					const styleElement = document.querySelector('#primaryColor');
					if (styleElement) {
						styleElement.remove();
					}
					const body = document.querySelector('body');
					const styleEl = document.createElement('style');
					const css = document.createTextNode(
						`:root { --kit-color-primary: ${color};}`
					);
					styleEl.setAttribute('id', 'primaryColor');
					styleEl.appendChild(css);
					body.appendChild(styleEl);
				};
				addStyles();
				this.store.dispatch(
					new SettingsActions.SetStateAction({
						primaryColor: color
					})
				);
			}
		};
		primaryColor();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private setTheme = (theme: string): void => {
		document.querySelector('html').setAttribute('data-kit-theme', theme);
		if (theme === 'default') {
			this.store.dispatch(
				new SettingsActions.SetStateAction({
					menuColor: 'light'
				})
			);
		}
		if (theme === 'dark') {
			this.store.dispatch(
				new SettingsActions.SetStateAction({
					menuColor: 'dark'
				})
			);
		}
	};
}
